<script lang="ts" setup>
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { useVModel } from "@vueuse/core"
import { computed, nextTick, watch } from "vue"
import { useI18n } from "vue-i18n"

import { useLangHelpers } from "@modules/energy-manager/composables/useLangHelpers"
import { PERIOD_SHORTCUT_BY_FREQUENCY } from "@modules/energy-manager/constants"
import moment from "moment"
import { computedDiffDays } from "@modules/energy-manager/helpers"

type Props = {
    period: [Date | null, Date | null]
    choice: string
    freq: string
    variable: string
}

const props = defineProps<Props>()
const emit = defineEmits(["update:period", "update:choice"])

// Composables
const emConsoCompareStore = useEmConsoCompareStore()
const { buildWarningFreq, buildErrorPeriod } = useLangHelpers()

//======================================================
// Gestion des traductions
//======================================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Période restreinte",
            content: ({ named }: { named: (key: string) => string }) => {
                return named("sentence")
            },
            calendar: {
                warn: {
                    title: "Période restreinte",
                },
                error: {
                    title: "Période dépassée",
                },
                content: ({ named }: { named: (key: string) => string }) => {
                    return named("sentence")
                },
            },
        },
    },
})

//======================================================
// Gestion des données du composant BaseCalendarPeriod
//======================================================
/**
 * V-model de la période
 */
const period = useVModel(props, "period", emit, {
    passive: true,
    deep: true,
})
/**
 * V-model du choix de période
 */
const choice = useVModel(props, "choice", emit)

//======================================================
// Gestion des messages d'erreur / avertissement du calendrier
//======================================================
/**
 * Vérifie si la fréquence a une restriction de période maximale
 */
const hasPeriodMaxRestriction = computed(() => {
    return !!emConsoCompareStore.getDisplayDataConfig?.config_periods[
        props.freq
    ]?.max
})

//======================================================
// Gestion des options (props) du BaseCalendarPeriod
//======================================================
/**
 * Paramètre "view" du calendrier en fonction de la fréquence
 */
const getViewByFreq = computed(() => {
    switch (props.freq) {
        case "monthly":
            return "month"
        // case "yearly":
        //     return "year"
        case "weekly":
            return "week"
        default:
            return "date"
    }
})

//======================================================
// Gestion des raccourcis du calendrier
//======================================================
/**
 * Paramètre "shortcuts" (liste des raccourcis de sélection de période) en fonction de la fréquence
 */
const getShortcutsPeriodsList = computed(() => {
    return PERIOD_SHORTCUT_BY_FREQUENCY[props.freq].map((option) => {
        // désactive l'option current_week si on est lundi de la semaine en cours
        if (option.id === "current_week" && moment().isoWeekday() === 1) {
            return {
                value: option.id,
                label: t(`shortcut_period.${option.id}`),
                disabled: true,
                period: option.period,
            }
        }

        return {
            value: option.id,
            label: t(`shortcut_period.${option.id}`),
            disabled: (
                emConsoCompareStore.getDisplayDataConfig?.config_periods[
                    props.freq
                ]?.disabled_shortcut ?? []
            ).includes(option.id),
            period: option.period,
        }
    })
})

/**
 * Quand l'utilisateur clique sur un raccourci de période
 * Met à jour les valeurs de "period" et "choice" par rapport au raccourci sélectionné
 * @param shortcutValue - Valeur du raccourci
 */
function onClickShortcut(shortcutValue: string) {
    const start_date = moment()
    start_date.set("h", 0).set("m", 0).set("s", 0).set("ms", 0)
    const end_date = moment()
    end_date.set("h", 0).set("m", 0).set("s", 0).set("ms", 0)

    // récupère la configuration de la période
    const periodConfig = getShortcutsPeriodsList.value.find(
        (item) => item.value === shortcutValue
    )?.period

    if (!periodConfig) return

    const quantity = periodConfig.quantity

    // Cas particulier de la période "current_week"
    if (shortcutValue === "current_week") {
        start_date.set("isoWeekday", 1)
        end_date.subtract(1, "days")
    }

    // Si le raccouris correspond à last_Xmonths en fréquence autre que daily ou hourly
    else if (
        shortcutValue.includes("month") &&
        !["hourly", "daily"].includes(props.freq)
    ) {
        // met au premier jour du dernier mois
        start_date.set("date", 1)
        start_date.subtract(quantity, "months")

        end_date.subtract(1, "months")
        if (quantity === 1) {
            end_date.set("date", end_date.daysInMonth())
        } else {
            end_date.set("date", 1)
        }
    }
    // cas où on demande les X derniers mois en fréquence daily ou hourly
    else if (
        shortcutValue.includes("month") &&
        ["hourly", "daily"].includes(props.freq)
    ) {
        // met la date de début de période au 1premier jour des X derniers mois
        start_date
            .set("date", 1)
            .subtract(quantity, "months")
            // dans le cas où on demande plus de 1 mois, on retire 1 mois en plus
            .subtract(quantity === 1 ? 0 : 1, "months")

        // si on demande 1 mois, on met le dernier jour du mois en cours
        // sinon on met le premier jour du mois précédent
        end_date
            .subtract(1, "months")
            .set("date", quantity === 1 ? end_date.daysInMonth() : 1)
    }
    // Si le raccouris correspond à last_Xweeks
    else if (shortcutValue.includes("week")) {
        start_date.subtract(quantity, "weeks")
        start_date.set("isoWeekday", 1)

        end_date.set("isoWeekday", 0)
        if (quantity === 1) {
            end_date.set("isoWeekday", 7)
        } else {
            end_date.set("isoWeekday", 1)
        }
    }
    // Si le raccouris correspond à last_Xdays
    else if (shortcutValue.includes("day")) {
        end_date.subtract(1, "days")
        start_date.subtract(quantity, "days")
    }
    // Si le raccouris correspond à last_Xyears
    else if (shortcutValue.includes("year")) {
        start_date.set({
            month: 0,
            date: 1,
            year: start_date.year() - quantity,
        })
        end_date.set({
            month: 11,
            date: 31,
            year: end_date.year() - 1,
        })
    }

    // gestion du custom
    else if (shortcutValue === "custom") {
        switch (props.freq) {
            case "monthly":
                // met au premier jour du dernier mois
                start_date.subtract(2, "months").set("date", 1)
                end_date.subtract(1, "months").set("date", 1)
                break
            case "weekly":
                // met au premier jour du dernier mois
                start_date.subtract(1, "week").set("isoWeekday", 1)
                end_date.subtract(1, "week").set("isoWeekday", 7)
                break
            default:
                // met 2 jours avant
                start_date.subtract(2, "days")
                end_date.subtract(1, "days")
                break
        }
    }

    // Met à jour les dates dans le calendar
    period.value = [start_date.toDate(), end_date.toDate()]

    // Met à jour le choix de la période
    choice.value = shortcutValue
}

/**
 * Écoute le changement de la fréquence
 * pour mettre à jour le choix de raccourci de période si celui-ci n'est pas disponible
 */
watch(
    () => props.freq,
    () => {
        // si le choix en cours n'est pas dans la liste des options disponibles alors
        // on le réinitialise avec la première option de la liste non désactivée
        if (
            !getShortcutsPeriodsList.value
                .map((item) => item.value)
                .filter((item) => item !== "custom")
                .includes(choice.value)
        ) {
            const firstNotDisabled = getShortcutsPeriodsList.value.find(
                (item) => !item.disabled && item.value !== "current_week"
            )
            choice.value = firstNotDisabled?.value ?? "custom"

            nextTick(() => {
                // Met à jour la période en fonction du choix
                onClickShortcut(choice.value)
            })
        }
    }
)

const getDiffDays = computed(() => {
    return computedDiffDays(period.value, getViewByFreq.value)
})

const hasPeriodExceedMaxRestriction = computed(() => {
    // Vérifie que la période
    const configFreq =
        emConsoCompareStore.getDisplayDataConfig?.config_periods[props.freq]
            ?.max

    if (!configFreq) return false

    // Vérifie que la période est inférieure ou égale à la période maximale

    switch (configFreq.unit) {
        case "week":
            return getDiffDays.value > configFreq?.quantity * 7
        case "month":
            return getDiffDays.value > configFreq?.quantity * 31
        case "year":
            return getDiffDays.value - 1 > configFreq?.quantity * 366
        default:
            return getDiffDays.value > configFreq?.quantity
    }
})
</script>

<template>
    <BaseCalendarPeriod
        v-model:period="period"
        v-model:choice="choice"
        class="dashboard-card-compare-period"
        :view="getViewByFreq"
        :shortcuts="
            getShortcutsPeriodsList.map((item) => ({
                value: item.value,
                label: item.label,
                disabled: item.disabled,
            }))
        "
        :error="hasPeriodExceedMaxRestriction"
        @click:shortcut="onClickShortcut"
    >
        <template #header>
            <div class="flex items-center">
                <BaseIcon icon="fal fa-circle-3" color="var(--p-primary-700)" />
                <!-- Message en cas de période limitée par la fréquence -->
                <BaseTooltipMessage
                    @click.prevent.stop
                    v-if="hasPeriodMaxRestriction"
                    severity="warn"
                    class="ml-2"
                    :title="t('title')"
                    :content="
                        t('content', {
                            sentence: buildWarningFreq(
                                emConsoCompareStore.getDisplayDataConfig
                                    ?.config_periods ?? {},
                                freq,
                                emConsoCompareStore.getSourcesCount
                            ),
                        })
                    "
                />
            </div>
        </template>
        <!-- Message dans le calendrier en cas de période limitée par la fréquence -->
        <template #calendar-message>
            <BaseAppMessage
                severity="warn"
                v-if="hasPeriodMaxRestriction && !hasPeriodExceedMaxRestriction"
            >
                <template #header>
                    {{ t("calendar.warn.title") }}
                </template>
                <p
                    v-html="
                        t('calendar.content', {
                            sentence: buildWarningFreq(
                                emConsoCompareStore.getDisplayDataConfig
                                    ?.config_periods ?? {},
                                freq,
                                emConsoCompareStore.getSourcesCount
                            ),
                        })
                    "
                ></p>
            </BaseAppMessage>
            <BaseAppMessage
                severity="error"
                v-else-if="hasPeriodExceedMaxRestriction"
            >
                <template #header>
                    {{ t("calendar.error.title") }}
                </template>
                <p
                    v-html="
                        t('calendar.content', {
                            sentence: buildErrorPeriod(
                                emConsoCompareStore.getDisplayDataConfig
                                    ?.config_periods ?? {},
                                freq,
                                emConsoCompareStore.getSourcesCount
                            ),
                        })
                    "
                ></p>
            </BaseAppMessage>
        </template>
    </BaseCalendarPeriod>
</template>
